import { useCallback } from "react";
import { useNavigate, useResolvedPath } from "react-router";

import MetaInfoProvider from "components/MetaInfoList/Provider";
import SpacePath from "components/SpacePath";
import TimestampMetaInfo from "components/Timestamp/MetaInfo";
import ViewHeaderTitle from "components/ViewHeader/Title";
import Badge from "ds/components/Badge";
import Button from "ds/components/Button";
import Tab from "ds/components/Tab";
import TabWithCounter from "ds/components/Tab/WithCounter";
import useCanManageVCSIntegration from "shared/VCS/access/useCanManageVCSIntegration";
import { VcsIntegration } from "types/generated";
import { useLayoutActions } from "views/Account/Integrations/hooks/useLayoutActions";
import { useLayoutHeading } from "views/Account/Integrations/hooks/useLayoutHeading";
import { useLayoutMeta } from "views/Account/Integrations/hooks/useLayoutMeta";
import { useLayoutNavigation } from "views/Account/Integrations/hooks/useLayoutNavigation";

import { showVCSIntegrationDetailsDrawer } from "../../VcsShared/components/Details";
import { getEditIntegrationURL } from "../../VcsShared/helpers";
import VCSIntegrationDropdownActions from "../DropdownActions";

type VCSIntegrationHeaderProps = {
  integration: VcsIntegration;
  totalEntitiesCount: number;
};

const VCSIntegrationHeader = ({ integration, totalEntitiesCount }: VCSIntegrationHeaderProps) => {
  const navigate = useNavigate();
  const resolvedPath = useResolvedPath("..");
  // FYI: incorrect type as integration.space can be undefined in certain circumstances
  const canManageVCSIntegrations = useCanManageVCSIntegration()(integration);

  const handleOpenDetailsDrawer = () => {
    showVCSIntegrationDetailsDrawer({
      item: integration,
      canEdit: canManageVCSIntegrations,
    });
  };

  const handleIntegrationEdit = useCallback(() => {
    if (integration?.id) {
      navigate(getEditIntegrationURL(integration.provider, integration.id));
    }
  }, [navigate, integration]);

  useLayoutHeading(
    <>
      <ViewHeaderTitle>{integration?.name}</ViewHeaderTitle>
      {integration?.isDefault && <Badge variant="gray" type="strong" text="DEFAULT" />}
    </>,
    integration?.name ?? ""
  );

  useLayoutActions(
    <>
      <Button variant="secondary" onPress={handleOpenDetailsDrawer}>
        Details
      </Button>
      {canManageVCSIntegrations && (
        <VCSIntegrationDropdownActions
          integration={integration}
          onEdit={handleIntegrationEdit}
          totalEntitiesCount={totalEntitiesCount}
        />
      )}
    </>
  );

  useLayoutMeta(
    integration ? (
      <>
        <SpacePath
          variant="meta"
          spaceId={integration?.space?.id}
          fallbackName={integration?.space?.name}
        />
        <MetaInfoProvider provider={integration?.provider} />
        <TimestampMetaInfo timestamp={integration?.updatedAt} />
      </>
    ) : null
  );

  useLayoutNavigation(
    <>
      <TabWithCounter end to={resolvedPath.pathname} label="Used by" count={totalEntitiesCount} />

      <Tab to={`${resolvedPath.pathname}/troubleshooting`} label="Troubleshooting" />
    </>
  );

  return null;
};

export default VCSIntegrationHeader;
